import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchAuthMe } from "../../redux/slices/auth";
import {
  LuChevronLeft,
  LuChevronRight,
  LuEye,
  LuEyeOff,
  LuKeyRound,
  LuLayoutGrid,
  LuMaximize,
  LuStickyNote,
  LuX,
} from "react-icons/lu";
import axios from "../../axios";
import SlideFrame from "./SlideFrame";
import WordPopover from "./WordPopover";
import { AnswersContext } from "./answers";
import { keyChannel } from "./KeyPage";
import * as styled from "./PresentPage.styled";
import { NoteText } from "./common";

const STAGE_W = 1280;
const STAGE_H = 720;

// Режим презентації: вчитель відкриває урок і показує екран у Google Meet.
// Клавіші: ← → / пробіл — гортання, A — відповіді, G — усі слайди, F — повний екран, T — нотатка вчителя.
function PresentPage() {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [error, setError] = useState(null);
  const [index, setIndex] = useState(0);
  const [showAnswers, setShowAnswers] = useState(false);
  const [gridOpen, setGridOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [scale, setScale] = useState(1);
  const [selection, setSelection] = useState(null);
  const [students, setStudents] = useState([]);
  const viewportRef = useRef(null);
  const dispatch = useDispatch();
  const { data: me } = useSelector((state) => state.auth);
  // Відповіді й нотатки — лише коли точно відомо, що це вчитель чи адмін (до завантаження — приховано)
  const isStudent = !["teacher", "admin", "manager"].includes(me?.userType);

  // Відповіді на уроці живуть, поки відкрита сторінка: повернулися на попередній слайд — усе на місці.
  // Новий урок — чисте сховище; перезавантаження сторінки теж скидає.
  const store = useMemo(() => {
    const values = {};
    return {
      values,
      review: false,
      save: (slideId, key, value) => {
        values[slideId] = { ...(values[slideId] || {}), [key]: value };
      },
      report: () => {},
    };
    // новий урок — нове сховище
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonId]);

  useEffect(() => {
    if (!me || typeof me !== "object" || !me.userType) dispatch(fetchAuthMe());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    axios
      .get(`platform-lessons/${lessonId}`)
      .then((res) => setLesson(res.data))
      .catch(() => setError("Урок не знайдено або немає доступу"));
  }, [lessonId]);

  // Учні вчителя — для кнопки «Додати в словник учня»
  useEffect(() => {
    if (isStudent || !me?._id) return;
    axios
      .get(`students-by-teacher/${me._id}`)
      .then((res) => setStudents(Array.isArray(res.data) ? res.data : []))
      .catch(() => setStudents([]));
  }, [isStudent, me?._id]);

  // Вчитель виділяє слово/фразу на слайді → переклад + додати в словник (без hover-підказок)
  const onStageMouseUp = (event) => {
    if (isStudent || event.target.closest("input, textarea, select")) return;
    const sel = window.getSelection();
    let text = sel?.toString().replace(/\s+/g, " ").trim();
    // Підписи розділів написані капсом через CSS — у словник кладемо звичайним регістром
    if (text && text === text.toUpperCase() && text.length > 1) text = text.toLowerCase();
    if (!text || text.length > 120 || !/[a-z]/i.test(text)) {
      setSelection(null);
      return;
    }
    const rect = sel.getRangeAt(0).getBoundingClientRect();
    setSelection({ text, x: rect.left + rect.width / 2, y: rect.bottom });
  };

  // Сцена 1280×720 масштабується під доступне місце — однаково виглядає на будь-якому екрані
  useLayoutEffect(() => {
    const fit = () => {
      const el = viewportRef.current;
      if (!el) return;
      setScale(Math.min(el.clientWidth / STAGE_W, el.clientHeight / STAGE_H));
    };
    fit();
    window.addEventListener("resize", fit);
    return () => window.removeEventListener("resize", fit);
  }, [lesson]);

  // Вікно ключів (окреме, учень його не бачить) іде за поточним слайдом
  const channelRef = useRef(null);
  const indexRef = useRef(0);
  indexRef.current = index;
  useEffect(() => {
    if (isStudent || typeof BroadcastChannel === "undefined") return undefined;
    const channel = new BroadcastChannel(keyChannel(lessonId));
    channel.onmessage = (event) => {
      if (event.data?.hello) channel.postMessage({ index: indexRef.current });
    };
    channelRef.current = channel;
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [lessonId, isStudent]);
  useEffect(() => {
    channelRef.current?.postMessage({ index });
  }, [index]);
  const openKeys = useCallback(
    () => window.open(`/present/${lessonId}/key`, `speaksy-key-${lessonId}`, "width=520,height=860,left=40,top=40"),
    [lessonId]
  );

  const total = lesson?.slides.length || 0;
  const go = useCallback(
    (next) => {
      setIndex((current) => Math.max(0, Math.min(total - 1, typeof next === "function" ? next(current) : next)));
      setShowAnswers(false);
      setSelection(null);
    },
    [total]
  );

  useEffect(() => {
    const onKey = (event) => {
      if (event.target.closest("input, textarea")) return;
      if (event.key === "ArrowRight" || event.key === " " || event.key === "PageDown") {
        event.preventDefault();
        go((i) => i + 1);
      } else if (event.key === "ArrowLeft" || event.key === "PageUp") {
        go((i) => i - 1);
      } else if (event.key.toLowerCase() === "a" && !isStudent) {
        setShowAnswers((value) => !value);
      } else if (event.key.toLowerCase() === "g") {
        setGridOpen((value) => !value);
      } else if (event.key.toLowerCase() === "k" && !isStudent) {
        openKeys();
      } else if (event.key.toLowerCase() === "t" && !isStudent) {
        setNoteOpen((value) => !value);
      } else if (event.key.toLowerCase() === "f") {
        if (document.fullscreenElement) document.exitFullscreen();
        else document.documentElement.requestFullscreen?.();
      } else if (event.key === "Escape") {
        setGridOpen(false);
        setSelection(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, isStudent, openKeys]);

  // Учень відкрив посилання на презентацію (стара вкладка, закладка) — урок веде вчитель, а ДЗ живе в кабінеті
  // Відкрито в новій вкладці (немає куди «назад») — у кабінет за роллю
  const closePage = () => {
    // idx — лічильник переходів React Router усередині платформи (history.length рахує й чужі сторінки вкладки)
    if ((window.history.state?.idx ?? 0) > 0) return navigate(-1);
    // Урок відкрито в окремій вкладці з «Матеріалів» — закриваємо її, кабінет лишився у вкладці поруч.
    // Якщо браузер не дав закрити (вкладку відкрили вручну) — у кабінет за роллю
    window.close();
    setTimeout(() => navigate(me?.userType === "student" ? "/student/homework" : me?.userType === "teacher" ? "/teacher/general" : "/"), 300);
  };

  if (error && me?.userType === "student") {
    return (
      <styled.Message>
        <span style={{ textAlign: "center", lineHeight: 1.7 }}>
          Цей урок проводить учитель на занятті.
          <br />
          Домашнє завдання до нього — у кабінеті: <a href="/student/homework">Домашнє завдання →</a>
        </span>
      </styled.Message>
    );
  }
  if (error) return <styled.Message>{error}</styled.Message>;
  if (!lesson) return <styled.Message>Завантажуємо урок…</styled.Message>;

  const slide = lesson.slides[index];

  return (
    <AnswersContext.Provider value={store}>
    <styled.Page>
      <styled.Viewport ref={viewportRef} onMouseUp={onStageMouseUp}>
        <styled.Stage style={{ width: STAGE_W, height: STAGE_H, transform: `scale(${scale})` }}>
          <SlideFrame key={slide.id} slide={slide} showAnswers={showAnswers} lesson={lesson} />
        </styled.Stage>
      </styled.Viewport>

      {!isStudent && selection && (
        <WordPopover key={selection.text} selection={selection} teacher={me} students={students} onClose={() => setSelection(null)} />
      )}

      {!isStudent && noteOpen && slide.teacherNote && (
        <styled.Note>
          <LuStickyNote size={18} /> <NoteText text={slide.teacherNote} />
        </styled.Note>
      )}

      <styled.Controls>
        <styled.Lesson title={lesson.source}>
          {/* teacherLevel — рівень, який перевіряє слайд (пробний урок): бачить лише вчитель; першим, щоб не зрізалось з довгою назвою */}
          {!isStudent && slide.teacherLevel && <styled.LevelBadge title="Рівень слайда (бачить лише вчитель)">🎯 {slide.teacherLevel}</styled.LevelBadge>}
          {lesson.number}. {lesson.title}
        </styled.Lesson>
        <styled.Nav>
          <styled.IconButton onClick={() => go((i) => i - 1)} disabled={index === 0} title="Previous (←)">
            <LuChevronLeft size={22} />
          </styled.IconButton>
          <styled.Counter onClick={() => setGridOpen(true)} title="All slides (G)">
            {index + 1} / {total}
          </styled.Counter>
          <styled.IconButton onClick={() => go((i) => i + 1)} disabled={index === total - 1} title="Next (→)">
            <LuChevronRight size={22} />
          </styled.IconButton>
        </styled.Nav>
        <styled.Tools>
          {!isStudent && (
            <>
              <styled.Tool $active={showAnswers} onClick={() => setShowAnswers((v) => !v)} title="Answers (A)">
                {showAnswers ? <LuEyeOff size={18} /> : <LuEye size={18} />} Answers
              </styled.Tool>
              <styled.Tool onClick={openKeys} title="Ключі в окремому вікні — у Meet показуйте лише вкладку з уроком (K)">
                <LuKeyRound size={18} /> Ключі
              </styled.Tool>
              <styled.Tool $active={noteOpen} onClick={() => setNoteOpen((v) => !v)} disabled={!slide.teacherNote} title="Teacher note (T)">
                <LuStickyNote size={18} />
              </styled.Tool>
            </>
          )}
          <styled.Tool onClick={() => setGridOpen(true)} title="All slides (G)">
            <LuLayoutGrid size={18} />
          </styled.Tool>
          <styled.Tool
            onClick={() => (document.fullscreenElement ? document.exitFullscreen() : document.documentElement.requestFullscreen?.())}
            title="Fullscreen (F)"
          >
            <LuMaximize size={18} />
          </styled.Tool>
          <styled.Tool onClick={closePage} title="Close">
            <LuX size={18} />
          </styled.Tool>
        </styled.Tools>
      </styled.Controls>

      {gridOpen && (
        <styled.Grid onClick={() => setGridOpen(false)}>
          <styled.GridInner onClick={(event) => event.stopPropagation()}>
            {lesson.slides.map((item, i) => (
              <styled.GridItem
                key={item.id}
                $current={i === index}
                onClick={() => {
                  go(i);
                  setGridOpen(false);
                }}
              >
                <span>{i + 1}</span>
                <small>{item.section || item.type}</small>
                <b>{item.title}</b>
              </styled.GridItem>
            ))}
          </styled.GridInner>
        </styled.Grid>
      )}
    </styled.Page>
    </AnswersContext.Provider>
  );
}

export default PresentPage;
